// Shared synth builders for multi-outcome (soccer 3-way) markets — used by
// the Value Dashboard's MultiOutcomeTable + panel wiring AND GameDetail's
// soccer moneyline. One event = three independent binary markets (Away win /
// Home win / Draw); these helpers project them into the binary GameRow
// shapes the shared renderers (GameRows, GameDetailPanel, TradeTicketBody)
// already understand.
import type { GameRow, MultiOutcomeEvent, SeriesOutcome, MarketSide, VenueBook } from './types';
import type { BetLineContext } from './GameDetailPanel';

/** An outcome resolved YES — the venue printed it at 100¢. */
function outcomeWon(o: SeriesOutcome | undefined): boolean {
  return o?.kalshi?.lastPriceCents === 100 || o?.polymarket?.lastPriceCents === 100;
}

/** The event's outcomes resolved to their away/home/draw roles. */
export function multiOutcomeRoles(event: MultiOutcomeEvent): {
  away?: SeriesOutcome;
  home?: SeriesOutcome;
  draw?: SeriesOutcome;
} {
  return {
    away: event.outcomes.find((o) => o.role === 'away'),
    home: event.outcomes.find((o) => o.role === 'home'),
    draw: event.outcomes.find((o) => o.role === 'draw'),
  };
}

/** Away/home pair as one binary-shaped GameRow: the away slot is the
 *  away-win market, the home slot the home-win market — the same shape
 *  GameLinesSection synthesizes for the NBA 1H winner pair. A venue's
 *  column renders only when it carries BOTH team markets. Keyed by the
 *  event key itself so score/consensus lookups (toHydraGameKey) work. */
export function multiOutcomePairRow(
  e: MultiOutcomeEvent,
  away: SeriesOutcome,
  home: SeriesOutcome,
): GameRow {
  return {
    key: e.key,
    sport: e.sport,
    date: e.date ?? '',
    timePt: e.timePt,
    startTimeMs: e.startTimeMs,
    gameState: e.gameState,
    finalWinnerSide:
      e.gameState === 'final'
        ? outcomeWon(away) ? 'away' : outcomeWon(home) ? 'home' : null
        : null,
    awayCode: e.awayCode,
    homeCode: e.homeCode,
    awayName: e.awayName,
    homeName: e.homeName,
    kalshi: away.kalshi && home.kalshi ? { away: away.kalshi, home: home.kalshi } : null,
    polymarket:
      away.polymarket && home.polymarket
        ? { away: away.polymarket, home: home.polymarket }
        : null,
  };
}

/** Draw as a singleSide GameRow — one market duplicated into both slots
 *  (only the away slot renders). Key matches the panel synth for this
 *  outcome so the expanded-row highlight lines up. */
export function multiOutcomeDrawRow(e: MultiOutcomeEvent, draw: SeriesOutcome): GameRow {
  const dupe = (b: VenueBook | null | undefined) => (b ? { away: b, home: b } : null);
  return {
    key: `${e.key}|multi|${draw.code}`,
    sport: e.sport,
    date: e.date ?? '',
    timePt: e.timePt,
    startTimeMs: e.startTimeMs,
    gameState: e.gameState,
    finalWinnerSide: e.gameState === 'final' && outcomeWon(draw) ? 'away' : null,
    awayCode: e.awayCode,
    homeCode: e.homeCode,
    awayName: draw.name || 'Draw',
    homeName: 'Not Draw',
    kalshi: dupe(draw.kalshi),
    polymarket: dupe(draw.polymarket),
  };
}

/** Per-outcome synth GameRow for the side panel + trade ticket: the chosen
 *  outcome's market duplicated into both slots, away = the outcome ("CRY"),
 *  home = its complement ("CRY does not win" / "Not Draw"). The `|multi|` key
 *  segment flips gameMarketKind to 'multi' so ticket labels read
 *  outcome-style, not team-vs-team. */
export function synthesizeFromMultiOutcome(event: MultiOutcomeEvent, outcome: SeriesOutcome): GameRow {
  const notLabel = outcome.role === 'draw' ? 'Not Draw' : `${outcome.name} does not win`;
  const sharedKalshi = outcome.kalshi
    ? { away: outcome.kalshi, home: outcome.kalshi }
    : null;
  const sharedPoly = outcome.polymarket
    ? { away: outcome.polymarket, home: outcome.polymarket }
    : null;
  return {
    key: `${event.key}|multi|${outcome.code}`,
    sport: event.sport,
    date: event.date ?? '',
    timePt: event.timePt,
    startTimeMs: event.startTimeMs,
    gameState: event.gameState,
    finalWinnerSide: null,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: outcome.name,
    homeName: notLabel,
    kalshi: sharedKalshi,
    polymarket: sharedPoly,
  };
}

export function buildMultiOutcomeBetContext(event: MultiOutcomeEvent, selectedOutcome: SeriesOutcome): BetLineContext {
  const selectedIdx = Math.max(0, event.outcomes.findIndex((o) => o.code === selectedOutcome.code));
  const titleFor = (side: MarketSide, idx: number) => {
    const outcome = event.outcomes[idx] ?? selectedOutcome;
    const yesLabel = outcome.name;
    const noLabel = outcome.role === 'draw' ? 'Not Draw' : `${outcome.name} does not win`;
    return (
      <>
        {side === 'yes' ? yesLabel : noLabel}
        <span className="text-muted-foreground"> · {event.title}</span>
      </>
    );
  };
  return {
    titleFor,
    chips: event.outcomes.map((outcome) => ({
      key: outcome.code,
      label: outcome.name,
      priceCents: outcome.kalshi?.priceCents ?? outcome.polymarket?.priceCents ?? null,
    })),
    selectedIdx,
    onSelectIdx: () => {},
    getGameForIdx: (idx: number) => {
      const outcome = event.outcomes[idx];
      return outcome ? synthesizeFromMultiOutcome(event, outcome) : null;
    },
  };
}
